import { z } from 'zod';
import { ISO_CURRENCY } from './proposal.js';
import { PaymentDtoSchema } from './payment.js';

// Invoice lifecycle.
//   draft         — staff is building it; not visible to client
//   issued        — published to client, awaiting payment
//   awaiting_wire — bank transfer initiated by client; awaiting receipt
//   received      — funds visible in Omega bank/Revolut; awaiting reconciliation
//   cleared       — reconciled by Finance; engagement counts it as paid
//   void          — cancelled, kept for audit (never deleted)
//   refunded      — funds returned after clearing (rare)
export const INVOICE_STATUSES = [
  'draft', 'issued', 'awaiting_wire', 'received', 'cleared', 'void', 'refunded',
] as const;
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

// Payment rails Omega offers. Card flows through Revolut Business; the bank
// rail is a per-invoice unique reference reconciled manually against the
// Omega operating account.
export const PAYMENT_RAILS = ['card', 'bank'] as const;
export const PaymentRailSchema = z.enum(PAYMENT_RAILS);
export type PaymentRail = z.infer<typeof PaymentRailSchema>;

// Input shape for POST /api/invoices — staff issues from an accepted proposal.
// Most fields are derived server-side; the client just picks the slice.
export const InvoiceIssueInputSchema = z.object({
  proposal_id: z.string().min(1),
  // Sub-amount when the proposal is split across multiple invoices (e.g. 50/50).
  // Defaults to the full remaining balance if omitted.
  amount_pence: z.number().int().positive().optional(),
  // Optional milestone reference (matches proposal.milestones[].label).
  milestone_label: z.string().max(200).optional(),
  // 14d / 30d / on-receipt — free-form line for the PDF, not a state machine.
  due_in_days: z.number().int().min(0).max(120).default(14),
  notes: z.string().max(2000).optional(),
});
export type InvoiceIssueInput = z.infer<typeof InvoiceIssueInputSchema>;

// Patch shape — only draft invoices can be edited. We allow re-targeting the
// milestone label, adjusting amount + due date.
export const InvoicePatchSchema = z.object({
  amount_pence: z.number().int().positive().optional(),
  milestone_label: z.string().max(200).nullable().optional(),
  due_in_days: z.number().int().min(0).max(120).optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type InvoicePatch = z.infer<typeof InvoicePatchSchema>;

// Mark-paid input — records a manual reconciliation event. Used by Finance
// when a bank transfer lands. Card flow writes through the Revolut webhook
// (Phase 5) and will not call this endpoint.
export const InvoiceMarkPaidSchema = z.object({
  rail: PaymentRailSchema,
  received_amount_pence: z.number().int().positive(),
  received_at: z.string().datetime().optional(),
  external_ref: z.string().max(200).optional(), // bank ref, Revolut payment id
  note: z.string().max(2000).optional(),
});
export type InvoiceMarkPaid = z.infer<typeof InvoiceMarkPaidSchema>;

// Public DTO.
export const InvoiceDtoSchema = z.object({
  id: z.string(),                 // INV-XXXX
  proposal_id: z.string(),
  request_id: z.string(),
  client_id: z.string(),
  client_name: z.string().optional(),

  status: InvoiceStatusSchema,

  milestone_label: z.string().nullable().optional(),
  amount_pence: z.number().int().nonnegative(),
  currency: z.enum(ISO_CURRENCY),
  notes: z.string().nullable().optional(),

  due_at: z.string(),             // computed from issued_at + due_in_days
  issued_at: z.string().nullable().optional(),
  paid_at: z.string().nullable().optional(),
  voided_at: z.string().nullable().optional(),

  payment_rail: PaymentRailSchema.nullable().optional(),
  received_amount_pence: z.number().int().nonnegative().nullable().optional(),
  external_ref: z.string().nullable().optional(),

  // Cumulative refunded amount in minor units (Phase 5f). Null on
  // never-refunded invoices; positive integer once one or more refunds
  // have been issued. When this equals received_amount_pence the invoice
  // status flips to 'refunded'.
  refunded_amount_pence: z.number().int().nonnegative().nullable().optional(),

  // Permanent bank-transfer reference an operator quotes to the client. Stable
  // for the lifetime of the invoice — used by Finance to reconcile.
  payment_reference: z.string(),

  // Snapshot of cmsLegal.version at issue time (REQ-008). Pins the invoice
  // to the disclaimer copy the client saw when it was issued; portal shows
  // a banner if cmsLegal has moved on. Null on rows issued before Phase 5a.
  disclaimers_version: z.string().nullable().optional(),

  // Per-attempt payment rows tied to this invoice (Phase 5+). Ordered by
  // created_at ascending — earliest attempt first. Empty array when no
  // payment has been initiated yet. Omitted on list responses; populated
  // only on GET /api/invoices/:id.
  payments: z.array(PaymentDtoSchema).optional(),

  // True if the client may use Pay-by-card on this invoice — gated on
  // KYC clearance, currency support in Revolut, and feature flag. Phase 5d
  // hides the CTA when false. Omitted on list responses.
  pay_card_enabled: z.boolean().optional(),

  created_by_staff_id: z.string(),
  marked_paid_by_staff_id: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type InvoiceDto = z.infer<typeof InvoiceDtoSchema>;
